interface ChatOutlineControllerOptions {
  button: HTMLElement | null;
  messages: HTMLElement | null;
}

interface OutlineItem {
  id: string;
  text: string;
  element: HTMLElement;
}

const OUTLINE_TEXT_LIMIT = 42;
let generatedOutlineId = 0;

function nextOutlineId(): string {
  generatedOutlineId += 1;
  return `chat-outline-message-${generatedOutlineId}`;
}

function getMessageText(element: HTMLElement): string {
  const datasetText = String(element.dataset.outlineText || '').trim();
  if (datasetText) return datasetText;
  const textNode = element.querySelector('.msg-text');
  return String(textNode?.textContent || '').replace(/\s+/g, ' ').trim();
}

function truncateText(text: string): string {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= OUTLINE_TEXT_LIMIT) return normalized;
  return `${normalized.slice(0, OUTLINE_TEXT_LIMIT)}…`;
}

function ensureOutlineId(element: HTMLElement): string {
  const existing = String(element.dataset.outlineId || '').trim();
  if (existing) return existing;
  const identity = String(element.dataset.messageIdentity || '').trim();
  const id = identity ? `chat-outline-message-${identity}` : nextOutlineId();
  element.dataset.outlineId = id;
  return id;
}

function collectUserMessages(messages: HTMLElement | null): OutlineItem[] {
  if (!messages) return [];
  return Array.from(messages.querySelectorAll<HTMLElement>('.msg.user'))
    .map((element) => ({ element, text: getMessageText(element), id: ensureOutlineId(element) }))
    .filter((item) => item.text.length > 0);
}

export function createChatOutlineController(options: ChatOutlineControllerOptions): { close: () => void; refresh: () => void } {
  const button = options.button;
  const messages = options.messages;
  const panel = document.createElement('div');
  panel.id = 'chat-outline-panel';
  panel.className = 'chat-outline-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'menu');
  panel.setAttribute('aria-label', '用户消息大纲');
  button?.insertAdjacentElement('afterend', panel);

  let observer: MutationObserver | null = null;
  const targetsByOutlineId = new Map<string, HTMLElement>();

  function isOpen(): boolean {
    return !panel.hidden;
  }

  function render(): void {
    const items = collectUserMessages(messages);
    targetsByOutlineId.clear();
    items.forEach((item) => targetsByOutlineId.set(item.id, item.element));
    panel.innerHTML = '';
    const header = document.createElement('div');
    header.className = 'chat-outline-header';
    header.textContent = '用户消息大纲';
    panel.appendChild(header);

    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'chat-outline-empty';
      empty.textContent = '暂无用户消息';
      panel.appendChild(empty);
      return;
    }

    const list = document.createElement('div');
    list.className = 'chat-outline-list';
    items.forEach((item, index) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'chat-outline-item';
      row.setAttribute('role', 'menuitem');
      row.dataset.targetOutlineId = item.id;
      row.title = item.text;

      const number = document.createElement('span');
      number.className = 'chat-outline-index';
      number.textContent = String(index + 1).padStart(2, '0');
      const label = document.createElement('span');
      label.className = 'chat-outline-text';
      label.textContent = truncateText(item.text);
      row.append(number, label);
      list.appendChild(row);
    });
    panel.appendChild(list);
  }

  function close(): void {
    panel.hidden = true;
    button?.setAttribute('aria-expanded', 'false');
    observer?.disconnect();
    observer = null;
  }

  function open(): void {
    render();
    panel.hidden = false;
    button?.setAttribute('aria-expanded', 'true');
    if (messages && !observer) {
      observer = new MutationObserver(() => {
        if (isOpen()) render();
      });
      observer.observe(messages, { childList: true });
    }
  }

  function scrollToOutlineId(outlineId: string): void {
    const target = targetsByOutlineId.get(outlineId) || null;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.remove('msg-outline-highlight');
    window.setTimeout(() => target.classList.add('msg-outline-highlight'), 20);
    window.setTimeout(() => target.classList.remove('msg-outline-highlight'), 1500);
    close();
  }

  button?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (isOpen()) close();
    else open();
  });

  panel.addEventListener('click', (event) => {
    event.stopPropagation();
    const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('.chat-outline-item');
    const outlineId = String(row?.dataset.targetOutlineId || '').trim();
    if (outlineId) scrollToOutlineId(outlineId);
  });

  document.addEventListener('click', (event) => {
    if (!isOpen()) return;
    const target = event.target as Node | null;
    if (target && (panel.contains(target) || button?.contains(target))) return;
    close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) close();
  });

  return { close, refresh: render };
}
